import type { FactorEntry } from "../services/productFactorsService";

export const ANALYZER_MARKET_TIME_ZONE = "America/New_York";

const MARKET_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: ANALYZER_MARKET_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const MARKET_WEEKDAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
  timeZone: ANALYZER_MARKET_TIME_ZONE,
  weekday: "short",
});

function datePartsToKey(parts: Intl.DateTimeFormatPart[]): string {
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : "";
}

export function toMarketDateKey(date: Date = new Date()): string {
  return datePartsToKey(MARKET_DATE_FORMATTER.formatToParts(date));
}

// Factor rows are authored as source UTC buckets; do not relabel intraday rows to market-local dates.
export function toAnalyzerDateKey(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toISOString().slice(0, 10);
}

function dateKeyToMarketNoon(dateKey: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));
}

function isDateKey(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function toSourceDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function isMarketWeekdayDateKey(dateKey: string): boolean {
  const marketNoon = dateKeyToMarketNoon(dateKey);
  if (!marketNoon) return false;
  const weekday = MARKET_WEEKDAY_FORMATTER.format(marketNoon);
  return weekday !== "Sat" && weekday !== "Sun";
}

export function isAnalyzerWeekendDateKey(dateKey: string): boolean {
  return isDateKey(dateKey) && !isMarketWeekdayDateKey(dateKey);
}

export function isCompletedMarketDateKey(dateKey: string, now: Date = new Date()): boolean {
  if (!isMarketWeekdayDateKey(dateKey)) return false;
  const currentMarketDateKey = toMarketDateKey(now);
  return Boolean(currentMarketDateKey) && dateKey < currentMarketDateKey;
}

export function isVisibleAnalyzerCalendarDateKey(
  dateKey: string,
  now: Date = new Date()
): boolean {
  if (!isDateKey(dateKey)) return false;
  if (isMarketWeekdayDateKey(dateKey)) return isCompletedMarketDateKey(dateKey, now);
  return dateKey <= toSourceDateKey(now);
}

export function filterCompletedMarketDayFactors<T extends FactorEntry>(
  factors: T[],
  now: Date = new Date()
): T[] {
  return factors.filter((factor) =>
    isCompletedMarketDateKey(toAnalyzerDateKey(factor.t), now)
  );
}

export function filterVisibleAnalyzerCalendarFactors<T extends FactorEntry>(
  factors: T[],
  now: Date = new Date()
): T[] {
  const currentSourceDateKey = toSourceDateKey(now);
  const nowMs = now.getTime();
  return factors.filter((factor) => {
    const dateKey = toAnalyzerDateKey(factor.t);
    if (!isVisibleAnalyzerCalendarDateKey(dateKey, now)) return false;
    const factorMs = new Date(factor.t).getTime();
    return dateKey !== currentSourceDateKey || Number.isNaN(factorMs) || factorMs <= nowMs;
  });
}
